import { AlertTriangle, ArrowRight, Check } from "lucide-react";

interface VersionDiffViewerProps {
  live: any;
  draft: any;
  fieldsToCompare: { key: string; label: string; isLongText?: boolean }[];
}

export function VersionDiffViewer({ live, draft, fieldsToCompare }: VersionDiffViewerProps) {
  if (!draft) return null;

  // Filter fields that actually have differences
  const changedFields = fieldsToCompare.filter((f) => {
    const liveVal = live ? live[f.key] : "";
    const draftVal = draft[f.key];
    return String(liveVal).trim() !== String(draftVal).trim();
  });

  if (changedFields.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-semibold text-left">
        <Check className="h-4.5 w-4.5" />
        <span>Staged draft matches live production version perfectly. No pending diffs.</span>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card/45 backdrop-blur-md rounded-2xl p-6 space-y-4 text-left">
      <div className="flex items-center gap-2 pb-2 border-b border-border/80">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <div>
          <h4 className="text-sm font-bold text-foreground">Staging Review Panel (Pending Unsaved Draft Changes)</h4>
          <p className="text-[11px] text-muted-foreground">Compare staged draft versions below before promoting to live production.</p>
        </div>
      </div>

      <div className="space-y-4 divide-y divide-border/40">
        {changedFields.map((field) => {
          const liveVal = live ? live[field.key] : "";
          const draftVal = draft[field.key];

          return (
            <div key={field.key} className="pt-4 first:pt-0 space-y-2">
              <h5 className="text-xs font-bold text-brand-primary uppercase tracking-wider">{field.label}</h5>

              {field.isLongText ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Live Side */}
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-muted-foreground uppercase">Live Version</div>
                    <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-xl text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto text-red-400">
                      {String(liveVal) || <span className="italic opacity-50">(empty)</span>}
                    </div>
                  </div>
                  {/* Draft Side */}
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-brand-primary uppercase flex items-center gap-1">
                      <span>Staged Draft</span>
                      <ArrowRight className="h-3 w-3" />
                    </div>
                    <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-xl text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto text-emerald-400">
                      {String(draftVal) || <span className="italic opacity-50">(empty)</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 p-2 bg-red-500/5 border border-red-500/10 rounded-lg text-red-400">
                    <span className="font-bold opacity-50 shrink-0">Live:</span>
                    <span className="truncate">{String(liveVal) || <span className="italic opacity-50">(empty)</span>}</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-emerald-500/5 border border-emerald-500/10 rounded-lg text-emerald-400">
                    <span className="font-bold opacity-50 shrink-0">Draft:</span>
                    <span className="truncate">{String(draftVal) || <span className="italic opacity-50">(empty)</span>}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
